import { fmtIDR } from "./util";

const TOTALS = [
    ["ready_stock_sales_idr", "Ready Stock · Nilai Penjualan"],
    ["ready_stock_payment_idr", "Ready Stock · Jumlah Pembayaran"],
    ["ready_stock_marketplace_pending_idr", "Ready Stock · Saldo Marketplace Tertunda"],
    ["ready_stock_shipping_idr", "Ready Stock · Ongkir"],
    ["po_new_sales_idr", "Order PO Baru · Nilai Penjualan"],
    ["po_new_shipping_idr", "Order PO Baru · Ongkir"],
    ["po_payment_idr", "Pembayaran PO · Jumlah Pembayaran"],
    ["po_shipping_paid_idr", "Pembayaran PO · Ongkir Dibayar"],
];
const GRAND = [
    ["total_sales_idr", "Total Nilai Penjualan"],
    ["total_payment_idr", "Total Pembayaran (tanpa ongkir)"],
    ["total_marketplace_pending_idr", "Saldo Marketplace Tertunda"],
    ["total_shipping_idr", "Total Ongkir"],
];

export function TotalsGrid({ totals }) {
    if (!totals) return null;
    return (
        <div className="space-y-3" data-testid="report-totals">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {GRAND.map(([k, label]) => (
                    <div key={k} className="bg-white border border-mist rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-400">{label}</div>
                        <div className="font-serif text-xl mt-1" data-testid={`total-${k}`}>{fmtIDR(totals[k])}</div>
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1.5 text-xs">
                {TOTALS.map(([k, label]) => (
                    <div key={k} className="flex justify-between border-b border-mist/70 py-1">
                        <span className="text-neutral-500">{label}</span>
                        <span className="text-ink tabular-nums" data-testid={`subtotal-${k}`}>{fmtIDR(totals[k])}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function PoImpact({ items }) {
    if (!items?.length) return null;
    return (
        <div data-testid="po-impact">
            <h3 className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-2">Pencocokan pembayaran PO</h3>
            <div className="overflow-x-auto border border-mist rounded-sm bg-white">
                <table className="w-full text-xs">
                    <thead className="bg-neutral-50 text-neutral-500">
                        <tr>
                            {["ID Order PO", "Sumber", "Nilai Penjualan", "Dibayar Sebelumnya", "Dibayar Sekarang", "Sisa Tagihan", "Sisa Ongkir"].map((h) => (
                                <th key={h} className="text-left font-normal px-3 py-2 whitespace-nowrap">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.po_order_id} className="border-t border-mist" data-testid={`po-impact-${p.po_order_id}`}>
                                <td className="px-3 py-2 font-mono">{p.po_order_id}</td>
                                <td className="px-3 py-2">{p.source === "existing" ? "Laporan sebelumnya" : "Laporan ini"}</td>
                                <td className="px-3 py-2 tabular-nums">{fmtIDR(p.sales_value_idr)}</td>
                                <td className="px-3 py-2 tabular-nums">{fmtIDR(p.paid_before_idr)}</td>
                                <td className="px-3 py-2 tabular-nums">{fmtIDR(p.paid_now_idr)}</td>
                                <td className="px-3 py-2 tabular-nums font-medium">{fmtIDR(p.outstanding_after_idr)}</td>
                                <td className="px-3 py-2 tabular-nums">{fmtIDR(p.shipping_outstanding_after_idr)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function cell(v, type) {
    if (v === null || v === undefined || v === "") return <span className="text-neutral-300">—</span>;
    if (type === "money") return fmtIDR(v);
    return String(v);
}

export function SheetTable({ sheet }) {
    return (
        <div data-testid={`preview-sheet-${sheet.key}`}>
            <h3 className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-2">
                {sheet.title} · {sheet.rows.length} baris
            </h3>
            {sheet.rows.length === 0 ? (
                <div className="text-xs text-neutral-400 border border-dashed border-mist rounded-sm px-3 py-3">Tidak ada data</div>
            ) : (
                <div className="overflow-x-auto border border-mist rounded-sm bg-white">
                    <table className="w-full text-xs">
                        <thead className="bg-neutral-50 text-neutral-500">
                            <tr>
                                <th className="text-left font-normal px-3 py-2">Baris</th>
                                {sheet.columns.map((c) => (
                                    <th key={c.key} className="text-left font-normal px-3 py-2 whitespace-nowrap">{c.header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sheet.rows.map((r, i) => (
                                <tr key={i} className="border-t border-mist">
                                    <td className="px-3 py-2 text-neutral-400">{r._row ?? r.source_row}</td>
                                    {sheet.columns.map((c) => (
                                        <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.type === "money" ? "tabular-nums" : ""}`}>
                                            {cell(r[c.key], c.type)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default function ReportPreview({ preview }) {
    if (!preview) return null;
    return (
        <div className="space-y-6" data-testid="report-preview">
            <TotalsGrid totals={preview.totals} />
            <PoImpact items={preview.po_impact} />
            {preview.sheets.map((s) => <SheetTable key={s.key} sheet={s} />)}
            {preview.admin_notes && (
                <div className="text-sm text-neutral-600 border-l-2 border-sage-dark pl-3" data-testid="preview-admin-notes">
                    {preview.admin_notes}
                </div>
            )}
        </div>
    );
}
